import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Divider,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import { postsApi } from '../services/api';
import { normalizeImageUrl } from '../services/imageUtils';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';

const POST_TYPES = [
  { value: 'POST', label: 'Post' },
  { value: 'STORY', label: 'Story' },
  { value: 'REEL', label: 'Reel' },
];

const AUDIENCES = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'FOLLOWERS', label: 'Followers' },
  { value: 'GARDEN', label: 'Garden' },
  { value: 'PRIVATE', label: 'Private' },
  { value: 'SYSTEM', label: 'System' },
];

const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

export default function PostDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [post, setPost] = useState(null);
  const [form, setForm] = useState({
    text: '',
    postType: 'POST',
    audience: 'PUBLIC',
    gardenId: '',
    isAnipaw: false,
    visible: true,
    dogAlertStatus: '',
  });

  const isDogAlert = ['LOST_DOG', 'STRAY_DOG'].includes(form.postType);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentsPage, setCommentsPage] = useState(0);
  const [commentsRowsPerPage, setCommentsRowsPerPage] = useState(20);
  const [commentsTotal, setCommentsTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await postsApi.getPostById(id);
      setPost(data);
      setForm({
        text: data?.text ?? '',
        postType: data?.postType || 'POST',
        audience: data?.audience || 'PUBLIC',
        gardenId: data?.garden?.id || '',
        isAnipaw: !!data?.isAnipaw,
        visible: data?.visible ?? true,
        dogAlertStatus: data?.dogAlertStatus || '',
      });
    } catch (e) {
      console.error('Failed to load post', e);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    setCommentsError('');
    try {
      const data = await postsApi.getPostComments(id, { page: commentsPage, size: commentsRowsPerPage });
      setComments(data?.content || []);
      setCommentsTotal(data?.totalElements || 0);
    } catch (e) {
      console.error('Failed to load comments', e);
      setCommentsError('Failed to load comments.');
    } finally {
      setCommentsLoading(false);
    }
  }, [id, commentsPage, commentsRowsPerPage]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSwitch = (e) => {
    const { name, checked } = e.target;
    setForm((f) => ({ ...f, [name]: checked }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        text: form.text,
        postType: form.postType,
        audience: form.audience,
        gardenId: form.audience === 'GARDEN' ? (form.gardenId || null) : null,
        isAnipaw: form.isAnipaw,
        visible: form.visible,
        dogAlertStatus: isDogAlert && form.dogAlertStatus ? form.dogAlertStatus : null,
      };
      const updated = await postsApi.updatePostAdmin(id, payload);
      setPost(updated);
      setSuccess('Post saved');
    } catch (e) {
      console.error('Failed to save post', e);
      setError('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    try {
      await postsApi.deletePostMediaAdmin(mediaId);
      setPost((prev) => ({
        ...prev,
        media: prev.media.filter((m) => m.id !== mediaId),
      }));
      setSuccess('Image deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error('Failed to delete media', e);
      setError('Failed to delete image.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    try {
      await postsApi.deleteCommentAdmin(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentsTotal((t) => Math.max(0, t - 1));
      setSuccess('Comment deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error('Failed to delete comment', e);
      setError('Failed to delete comment.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/posts')}>Back to Posts</Button>
        <Typography variant="h5">Post Details #{id}</Typography>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Author + alert status summary */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }} alignItems="flex-start">
              {post?.user && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Author</Typography>
                  <MuiLink component={RouterLink} to={`/users/${post.user.id}`} underline="hover">
                    <Typography variant="subtitle1">{post.user.name || post.user.email || `#${post.user.id}`}</Typography>
                  </MuiLink>
                </Box>
              )}
              {isDogAlert && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Alert Status</Typography>
                  {post?.dogAlertStatus ? (
                    <Chip
                      label={post.dogAlertStatus}
                      color={post.dogAlertStatus === 'ACTIVE' ? 'warning' : 'default'}
                      variant={post.dogAlertStatus === 'ACTIVE' ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">Not set</Typography>
                  )}
                </Box>
              )}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Images */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Images</Typography>
              {post?.media && post.media.length > 0 ? (
                <ImageList cols={3} gap={8} sx={{ mb: 2 }}>
                  {post.media.map((m, idx) => (
                    <ImageListItem key={m.id ?? idx}>
                      <img
                        src={normalizeImageUrl(m.mediumUrl)}
                        alt={m.name}
                        loading="lazy"
                        style={{ borderRadius: 8, width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {m.id && (
                        <ImageListItemBar
                          position="top"
                          actionIcon={
                            <Tooltip title="Delete image">
                              <IconButton
                                size="small"
                                sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.45)', m: 0.5 }}
                                onClick={() => handleDeleteMedia(m.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                          actionPosition="right"
                          sx={{ background: 'transparent' }}
                        />
                      )}
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No images attached to this post.
                </Typography>
              )}
            </Box>

            {/* Videos */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Videos</Typography>
              {post?.videoMedia && post.videoMedia.length > 0 ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {post.videoMedia.map((v, idx) => (
                    <Box key={`${v.name}-${idx}`} sx={{ width: { xs: '100%', sm: 280 }, borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
                      <video src={v.url} controls style={{ width: '100%', height: 'auto' }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{v.name}</Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No videos attached to this post.</Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Edit form */}
            <Box component="form" noValidate autoComplete="off">
              <TextField
                label="Text"
                name="text"
                value={form.text}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                sx={{ mb: 2 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField select label="Post Type" name="postType" value={form.postType} onChange={handleChange} fullWidth>
                  {POST_TYPES.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </TextField>
                <TextField select label="Audience" name="audience" value={form.audience} onChange={handleChange} fullWidth>
                  {AUDIENCES.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </TextField>
                {isDogAlert && (
                  <TextField
                    select
                    label="Alert Status"
                    name="dogAlertStatus"
                    value={form.dogAlertStatus}
                    onChange={handleChange}
                    fullWidth
                  >
                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                    <MenuItem value="RESOLVED">RESOLVED</MenuItem>
                  </TextField>
                )}
              </Stack>

              {form.audience === 'GARDEN' && (
                <TextField
                  label="Garden ID"
                  name="gardenId"
                  value={form.gardenId}
                  onChange={handleChange}
                  type="number"
                  fullWidth
                  sx={{ mb: 2 }}
                  helperText="Enter the Dog Garden ID related to this post"
                />
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <FormControlLabel control={<Switch checked={form.isAnipaw} onChange={handleSwitch} name="isAnipaw" />} label="isAnipaw" />
                <FormControlLabel control={<Switch checked={form.visible} onChange={handleSwitch} name="visible" />} label="Visible" />
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outlined" color="secondary" onClick={load} disabled={saving}>
                  Reset
                </Button>
              </Stack>
            </Box>
          </Paper>

          {/* Comments section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Comments{commentsTotal > 0 ? ` (${commentsTotal})` : ''}
            </Typography>

            {commentsError && <Alert severity="error" sx={{ mb: 2 }}>{commentsError}</Alert>}

            {commentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
            ) : comments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No comments yet.</Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Author</TableCell>
                        <TableCell>Comment</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Delete</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comments.map((c) => (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            {c.user?.id ? (
                              <MuiLink component={RouterLink} to={`/users/${c.user.id}`} underline="hover">
                                {c.user.name || c.user.email || `#${c.user.id}`}
                              </MuiLink>
                            ) : '—'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 420, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {c.text}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(c.createdTimestamp)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete comment">
                              <IconButton size="small" color="error" onClick={() => handleDeleteComment(c.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={commentsTotal}
                  rowsPerPage={commentsRowsPerPage}
                  page={commentsPage}
                  onPageChange={(_, p) => setCommentsPage(p)}
                  onRowsPerPageChange={(e) => { setCommentsRowsPerPage(parseInt(e.target.value, 10)); setCommentsPage(0); }}
                />
              </>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
}
